import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp } from "../../../components";
import { useGetSubscriptionsQuery } from "../../../store/auth/authApi";
import EmptyTable from "../../../components/Common/EmptyTable.jsx";
import SubscriptionModal from "./SubscriptionModal";

function SubscriptionsListSkeleton({ cards = 6 }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      aria-busy="true"
      aria-label="Loading subscriptions"
    >
      {Array.from({ length: cards }).map((_, i) => (
        <div
          key={i}
          className="border-0 shadow p-6 rounded-xl bg-white animate-pulse"
        >
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-gray-200 mb-4" />
            <div className="h-6 bg-gray-200 rounded w-40 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-28 mb-4" />
            <div className="h-10 bg-gray-200 rounded-md w-full max-w-[14rem]" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SubscriptionsList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const navigate = useNavigate();

  // Subscriptions data
  const {
    data: subscriptionsData,
    isLoading: subscriptionsLoading,
    refetch: refetchSubscriptions,
  } = useGetSubscriptionsQuery();

  // Filter subscriptions based on search query
  const filteredSubscriptions = Array.isArray(subscriptionsData?.data)
    ? subscriptionsData.data.filter((subscription) => {
        if (!searchQuery.trim()) return true;

        const query = searchQuery.toLowerCase();
        return (
          subscription.name?.toLowerCase().includes(query) ||
          subscription.slug?.toLowerCase().includes(query) ||
          subscription.supportService?.toLowerCase().includes(query)
        );
      })
    : [];

  const subscriptions = filteredSubscriptions;

  const handleCreateSubscription = () => {
    setEditingSubscription(null);
    setIsModalOpen(true);
  };

  const handleEditSubscription = (subscription) => {
    setEditingSubscription(subscription);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSubscription(null);
  };

  const handleModalSuccess = () => {
    refetchSubscriptions();
  };

  const renderSubscriptionCards = (subscriptions) => {
    return subscriptions.map((subscription, index) => (
      <CardComp
        key={subscription.id || index}
        cardClassName="border-0 shadow p-6 rounded-xl bg-white mb-4 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => {
          console.log("Navigating to subscription ID:", subscription.id);
          navigate(`/subscriptions/${subscription.id}`);
        }}
      >
        <div className="flex flex-col items-center text-center">
          {/* Subscription Icon */}
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{
              backgroundColor:
                subscription.name === "Ruby"
                  ? "#ef4444"
                  : subscription.name === "Silver"
                  ? "#6b7280"
                  : "#10b981",
            }}
          >
            {subscription.name === "Ruby" && (
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            )}
            {subscription.name === "Silver" && (
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
              </svg>
            )}
            {subscription.name === "Gold" && (
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            )}
          </div>

          {/* Subscription Name */}
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {subscription.name} Plan
          </h3>

          {/* Subscriber + module counts */}
          <p className="text-sm text-gray-600 mb-1">
            {subscription.subscribers ?? 0} Subscribers
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {(subscription.modules || []).length} modules assigned
          </p>

          {/* Edit Button */}
          <button
            className="bg-[#8B313A] text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleEditSubscription(subscription);
            }}
          >
            Edit subscription plan
          </button>
        </div>
      </CardComp>
    ));
  };

  return (
    <div className="min-h-screen p-6">
      <div className="bg-white rounded-xl p-4 mb-6">
        <h1 className="text-2xl font-bold text-[#45464e]">Subscriptions</h1>
      </div>

      {/* Overview Section */}
      <div className="mt-5 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Subscriptions Card */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 shadow-sm text-white">
            <div className="bg-white rounded-xl p-4 mb-4 w-64">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-gray-900">
                    {subscriptionsData?.data?.length || 0}
                  </p>
                  <p className="text-sm text-gray-500">
                    Total subscription created
                  </p>
                </div>
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 bg-pink-200 rounded flex items-center justify-center">
                    <span className="text-pink-600 font-bold text-sm">101</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Add new subscriptions to the list above
            </p>
            <button
              onClick={handleCreateSubscription}
              className="bg-[#8B313A] text-white px-4 py-2 rounded-md hover:bg-red-700 w-full"
            >
              Create subscription
            </button>
          </div>

          {/* Recent Transactions Card */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Recent Transactions
            </h3>
            <div className="space-y-3">
              {/* Sample transaction - you can replace with real data */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      Olaoluwa C&C Starter plan
                    </p>
                    <p className="text-xs text-gray-500">
                      22 Sep 2019 • 01:36AM
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">
                    NGN 1,000.00
                  </p>
                  <p className="text-xs text-gray-500">Subscription renewal</p>
                </div>
              </div>
              {/* Add more sample transactions or fetch from API */}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Lists Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Subscription Lists
          </h2>
          <div className="flex items-center space-x-4">
            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search subscriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8B313A] focus:border-transparent w-64"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {subscriptionsLoading ? (
          <SubscriptionsListSkeleton cards={6} />
        ) : subscriptions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {renderSubscriptionCards(subscriptions)}
          </div>
        ) : (
          <EmptyTable
            Message={
              searchQuery.trim()
                ? `No subscriptions found matching "${searchQuery}"`
                : "No subscriptions found"
            }
          />
        )}
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        subscription={editingSubscription}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}
